import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, useMultiStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {PRODUCT_BADGE_DETAILS} from '@salesforce/retail-react-app/app/constants'
import ProductTileRecommended from './partials/product-tile-recommended'
import {isHydrated, remToPxConverter} from '../../utils/utils'
import {breakpoints} from '../../theme/_utils'
import {BRAND_SHORT_LABEL} from '../../brand'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceDataUseProduct} from '../../utils/product-utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
// import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'

const RecommendedProducts = ({
    badgeDetails = PRODUCT_BADGE_DETAILS,
    variant,
    tileImageVariant = 'default',
    title,
    products = [],
    prefix = 'default',
    gtmCarouselName,
}) => {
    const styles = useMultiStyleConfig('RecommendedProducts', {variant})
    const {addLayer} = useGoogleTagManager()
    const p = products.join(',')
    const _isHydrated = isHydrated()
    const [isMobile] = useMediaQuery(`(max-width: ${remToPxConverter(breakpoints.md) - 1}px)`)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const containerRef = useRef(null)
    const isDraggingRef = useRef(false)

    const handleMouseDown = (e) => {
        e.preventDefault()
        if (!containerRef.current) return
        isDraggingRef.current = true
        setTimeout(() => {
            setIsDragging(true)
        }, 100)
        setStartX(e.pageX - containerRef.current.offsetLeft)
        setScrollLeft(containerRef.current.scrollLeft)
    }

    const handleMouseMove = (e) => {
        if (!isDraggingRef.current || !containerRef.current) return
        e.preventDefault()
        const x = e.pageX - containerRef.current.offsetLeft
        const walk = (x - startX) * 1.25
        containerRef.current.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
        isDraggingRef.current = false
        setIsDragging(false)
    }

    const {data: recommendedProducts, isLoading} = useProducts({
        parameters: {
            ids: p,
            allImages: true,
            perPricebook: true,
            expand: [
                'availability',
                'links',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
            ],
        },
    })

    useEffect(() => {
        if (recommendedProducts?.data && !isLoading && _isHydrated) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            addLayer(
                item_gtm_event.bind(this, {
                    event: 'view_item_list',
                    item_list_name: gtmCarouselName,
                    items: recommendedProducts?.data?.map((product, i) => ({
                        item_name: product?.c_defaultName,
                        item_id: product?.variants?.[0]?.productId,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.id || product?.c_subModelIdentifier,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceDataUseProduct(product)?.isOnSale
                            ? 'reduced'
                            : 'fullprice',
                        rating_points: '',
                        erp_category: product?.c_materialGroup || product?.c_MaterialGroup,
                        prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                        index: i + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    })),
                }),
            )
        }
    }, [recommendedProducts?.data, isLoading, _isHydrated])

    if (isLoading) return <></>
    if (!recommendedProducts?.data || recommendedProducts?.data?.length === 0) return <></>

    const filteredProducts = recommendedProducts?.data?.filter((product) => {
        return product?.variants?.length > 0
    })

    if (!filteredProducts || filteredProducts?.length === 0) return <></>

    return (
        <Flex
            {...styles.outerContainer}
            data-testid="recommended-products"
            id={
                prefix === 'frequently-bought-together'
                    ? 'js-pdp-accessories-async'
                    : prefix === 'yaml'
                    ? 'js-pdp-compare-async'
                    : undefined
            }
            className={`recommended-products-${prefix} ${
                prefix === 'cart'
                    ? isMobile
                        ? 'dln-cart__noticeBox'
                        : BRAND_SHORT_LABEL === 'DL'
                        ? 'dln-cart__content button.dln-btn'
                        : 'dln-cart__items'
                    : ''
            } ${
                prefix === 'frequently-bought-together'
                    ? BRAND_SHORT_LABEL === 'DL'
                        ? 'js-sfmc-personalization-pdp-fbt-bundle js-sfmc-personalization-pdp-fbt-no-bundle'
                        : 'ken-editorialcontent'
                    : ''
            } ${prefix === 'yaml' ? 'js-pdp-related-products-async' : ''}`}
        >
            <Text
                {...styles.headerLabel}
                data-testid="recommended-products-title"
                className={`recommended-products-title-${prefix}`}
            >
                {title}
            </Text>
            <Flex
                data-testid="recommended-products-scroll"
                className={`recommended-products-scroll-${prefix}`}
                {...styles.outerContainerScroll}
            >
                <Flex sx={styles.productsContainer}>
                    <Flex
                        ref={containerRef}
                        sx={{
                            ...styles.scrollableContainer,
                            ...styles.noSelect,
                            WebkitUserDrag: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onMouseMove={handleMouseMove}
                    >
                        {filteredProducts?.map((product, i) => (
                            <ProductTileRecommended
                                key={i}
                                variant={variant}
                                badgeDetails={badgeDetails}
                                product={product}
                                tileImageVariant={tileImageVariant}
                                prefix={prefix}
                                productIndex={i}
                                style={{WebkitUserDrag: 'none'}}
                                isDragging={isDragging}
                            />
                        ))}
                    </Flex>
                </Flex>
            </Flex>
        </Flex>
    )
}

RecommendedProducts.propTypes = {
    badgeDetails: PropTypes.array,
    variant: PropTypes.string,
    tileImageVariant: PropTypes.string,
    title: PropTypes.node,
    products: PropTypes.array,
    prefix: PropTypes.string,
    gtmCarouselName: PropTypes.string,
}

export default RecommendedProducts
